<script setup>
// Estúdio Social — Editor de post (Task 11). Dialog reutilizável: criar do
// zero, editar rascunho existente (prop `postId`, ou `open(id)` em runtime)
// e o fluxo de geração por IA (brief + tom + imagem opcional). Segue a
// direção visual da Task 10: o badge de plataforma continua sendo a única
// cor de marca saturada do módulo — o status do post usa o vocabulário
// semântico n-* (draft=slate, approved=blue, scheduled/publishing=amber,
// published=teal, failed/partial=ruby), nunca a cor da rede.
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import socialAPI from 'dashboard/api/social';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { SOCIAL_PLATFORMS } from './constants';
import FeedPreview from './FeedPreview.vue';

const props = defineProps({
  postId: { type: [String, Number], default: null },
});

const emit = defineEmits(['saved', 'closed']);

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const OBJECTIVE_OPTIONS = ['sell', 'engage', 'educate', 'announce'];
const TONE_OPTIONS = ['professional', 'casual', 'salesy', 'inspiring'];
const FORMAT_OPTIONS = ['single', 'carousel'];
const STATUS_TONES = {
  draft: 'border-n-slate-6 bg-n-slate-3 text-n-slate-11',
  approved: 'border-n-blue-6 bg-n-blue-3 text-n-blue-11',
  scheduled: 'border-n-amber-6 bg-n-amber-3 text-n-amber-11',
  publishing: 'border-n-amber-6 bg-n-amber-3 text-n-amber-11',
  published: 'border-n-teal-6 bg-n-teal-3 text-n-teal-11',
  failed: 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11',
  partial: 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11',
};

const dialogRef = ref(null);
const fileInputRef = ref(null);

const close = () => {
  dialogRef.value?.close();
};

// -- Post atual (novo ou carregado) ---------------------------------------
const currentPostId = ref(props.postId || null);
const status = ref('draft');
const content = ref('');
const scheduledAt = ref(''); // valor do <input type="datetime-local">
const selectedAccountIds = ref([]);
const mediaFile = ref(null); // File local pendente de upload
// Campos de PRODUÇÃO (pedido do social media da conta 1, 26/08): título
// (identificação no calendário), pipeline interno, link do Drive e o modo
// de criação — 'ia' (fluxo completo) ou 'manual' (briefing+legenda+criativo).
const postTitle = ref('');
const productionStatus = ref('');
const driveLink = ref('');
const manualBriefing = ref('');
const creationMode = ref(null); // null = escolhendo | 'ia' | 'manual'
const PRODUCTION_STATUSES = [
  'briefing',
  'gravacao',
  'edicao',
  'aprovacao',
  'finalizado',
];
const mediaPreviewUrl = ref(''); // blob (mediaFile) OU media_url do post salvo
// Proveniência da imagem em exibição — 'ai' | 'manual' | null. É estado de
// SESSÃO do dialog (não do post: o backend não guarda "a mídia atual veio
// da IA ou foi trocada depois"), por isso NUNCA é derivado de `hasGenerated`
// nem zerado por `persist()` — só muda quando o usuário gera (generate) ou
// troca a imagem manualmente (onFileChange/removeLocalMedia) NESTA sessão.
// Ao carregar um post existente, o melhor sinal disponível é
// `ai_params.generated_image`, sabendo que fica desatualizado se a imagem
// for trocada — o que já é coberto pelas próprias transições abaixo.
const mediaProvenance = ref(null);
// Vídeo (Reels): o upload aceita image/* e video/* — o tipo guia a prévia e
// bloqueia o uso de vídeo como referência da IA de imagem.
const mediaIsVideo = ref(false);

// -- IA ---------------------------------------------------------------------
const objective = ref('sell');
const brief = ref('');
const offerCta = ref('');
const tone = ref('professional');
const postFormat = ref('single');
const carouselCards = ref(3);
const generateImage = ref(true);
const hasGenerated = ref(false);
const variations = ref([]);
const chosenVariation = ref(0);
const generationStatus = ref('ready');
const carouselMediaUrls = ref([]);
let pollingTimer = null;
// Id do post criado pela geração de IA NESTA sessão do dialog, ainda não
// salvo nem editado manualmente pelo usuário. generate() SEMPRE cria um post
// NOVO no backend — sem isso, cada "Gerar de novo" sem salvar deixava um
// rascunho órfão no calendário. Zerado ao salvar (persist) ou ao editar
// manualmente (conteúdo/mídia/redes) NESTA sessão; a partir daí o rascunho
// deixa de ser "descartável" e regenerar não apaga mais nada.
const unsavedGeneratedPostId = ref(null);
// Snapshot do texto gerado, pra distinguir "content mudou porque a IA gerou"
// de "content mudou porque o usuário editou à mão" no watcher abaixo (os
// dois passam pelo mesmo ref content, então não dá pra usar só o valor).
let unsavedGeneratedContentSnapshot = null;

watch(content, newValue => {
  if (
    unsavedGeneratedPostId.value &&
    newValue !== unsavedGeneratedContentSnapshot
  ) {
    unsavedGeneratedPostId.value = null;
  }
});

// -- Contas conectadas --------------------------------------------------------
const connectedAccounts = ref([]);
const isLoadingAccounts = ref(false);
const accountsLoadError = ref('');

// -- Estados de carregamento/erro --------------------------------------------
const isLoadingPost = ref(false);
const isGenerating = ref(false);
const isSavingDraft = ref(false);
const isApproving = ref(false);
const isPublishing = ref(false);

const loadErrorMessage = ref('');
const generateErrorMessage = ref('');
const saveErrorMessage = ref('');
const actionErrorMessage = ref('');

const isEditing = computed(() => !!currentPostId.value);
const dialogTitle = computed(() =>
  isEditing.value
    ? t('SOCIAL_STUDIO.EDITOR.TITLE_EDIT')
    : t('SOCIAL_STUDIO.EDITOR.TITLE_NEW')
);
const canSave = computed(() => content.value.trim().length > 0);
const canApprove = computed(
  () => isAdmin.value && status.value === 'draft' && isEditing.value
);
const canPublish = computed(
  () =>
    isAdmin.value &&
    status.value === 'approved' &&
    isEditing.value &&
    selectedAccountIds.value.length > 0
);
const isLocalMediaFile = computed(() => !!mediaFile.value);
const showAiGeneratedBadge = computed(() => mediaProvenance.value === 'ai');
const publishActionLabel = computed(() =>
  scheduledAt.value
    ? t('SOCIAL_STUDIO.EDITOR.ACTIONS.SCHEDULE')
    : t('SOCIAL_STUDIO.EDITOR.ACTIONS.PUBLISH_NOW')
);
const generateButtonLabel = computed(() =>
  hasGenerated.value
    ? t('SOCIAL_STUDIO.EDITOR.AI.REGENERATE_BUTTON')
    : t('SOCIAL_STUDIO.EDITOR.AI.GENERATE_BUTTON')
);
const generatingHint = computed(() => {
  if (postFormat.value === 'carousel') {
    return t('SOCIAL_STUDIO.EDITOR.AI.GENERATING_HINT_CAROUSEL', {
      count: carouselCards.value,
    });
  }
  return generateImage.value
    ? t('SOCIAL_STUDIO.EDITOR.AI.GENERATING_HINT_WITH_IMAGE')
    : t('SOCIAL_STUDIO.EDITOR.AI.GENERATING_HINT_TEXT_ONLY');
});
const generationCostLabel = computed(() =>
  postFormat.value === 'carousel'
    ? t('SOCIAL_STUDIO.EDITOR.AI.GENERATE_CAROUSEL_BUTTON', {
        count: carouselCards.value,
      })
    : generateButtonLabel.value
);
const statusTone = computed(
  () => STATUS_TONES[status.value] || STATUS_TONES.draft
);
const previewAccount = computed(() => {
  const selectedId = selectedAccountIds.value[0];
  return (
    connectedAccounts.value.find(account => account.id === selectedId) ||
    connectedAccounts.value[0]
  );
});
const previewMediaUrls = computed(() => {
  if (carouselMediaUrls.value.length) return carouselMediaUrls.value;
  return mediaPreviewUrl.value ? [mediaPreviewUrl.value] : [];
});

const extractErrorMessage = (error, fallback) =>
  error.response?.data?.error || error.response?.data?.errors?.[0] || fallback;

// Exibe o horário LOCAL do navegador (o que o usuário espera ver e reenviar
// sem deslocamento) — NÃO usar toISOString().slice(0,16) aqui: isso mostra o
// horário em UTC, e como o submit (new Date(local).toISOString()) interpreta
// o valor do input como local, cada round-trip carregar→salvar deslocaria o
// horário pelo offset do fuso (BRT = -3h). Ver task-11-report.md, achado 2.
const formatDateTimeForInput = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const platformMeta = key => SOCIAL_PLATFORMS.find(p => p.key === key);

const resetState = () => {
  if (pollingTimer) window.clearTimeout(pollingTimer);
  pollingTimer = null;
  currentPostId.value = null;
  status.value = 'draft';
  content.value = '';
  scheduledAt.value = '';
  selectedAccountIds.value = [];
  mediaFile.value = null;
  mediaPreviewUrl.value = '';
  mediaProvenance.value = null;
  objective.value = 'sell';
  brief.value = '';
  offerCta.value = '';
  tone.value = 'professional';
  postFormat.value = 'single';
  carouselCards.value = 3;
  generateImage.value = true;
  hasGenerated.value = false;
  variations.value = [];
  chosenVariation.value = 0;
  generationStatus.value = 'ready';
  carouselMediaUrls.value = [];
  unsavedGeneratedPostId.value = null;
  unsavedGeneratedContentSnapshot = null;
  loadErrorMessage.value = '';
  generateErrorMessage.value = '';
  saveErrorMessage.value = '';
  actionErrorMessage.value = '';
};

const fetchAccounts = async () => {
  isLoadingAccounts.value = true;
  accountsLoadError.value = '';
  try {
    const { data } = await socialAPI.accounts();
    connectedAccounts.value = (data.accounts || []).filter(
      a => a.status === 'connected'
    );
  } catch (error) {
    accountsLoadError.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.ACCOUNTS_LOAD')
    );
  } finally {
    isLoadingAccounts.value = false;
  }
};

const applyLoadedPost = data => {
  currentPostId.value = data.id;
  status.value = data.status;
  content.value = data.content || '';
  postTitle.value = data.title || '';
  productionStatus.value = data.production_status || '';
  driveLink.value = data.drive_link || '';
  manualBriefing.value = data.ai_params?.briefing || '';
  // Editando post existente: sem escolha de modo — briefing manual salvo
  // indica criação manual, senão mostra o fluxo de IA.
  creationMode.value = data.ai_params?.briefing ? 'manual' : 'ia';
  scheduledAt.value = formatDateTimeForInput(data.scheduled_at);
  selectedAccountIds.value = (data.platforms || [])
    .map(p => p.social_account_id)
    .filter(id => id !== undefined && id !== null);
  carouselMediaUrls.value = data.medias_urls || [];
  mediaPreviewUrl.value = data.media_url || carouselMediaUrls.value[0] || '';
  mediaFile.value = null;
  mediaIsVideo.value = !!data.media_content_type?.startsWith('video/');
  // Sinal disponível no carregamento: ai_params.generated_image. Fica
  // desatualizado se a imagem for trocada depois — mas a partir daqui
  // qualquer troca NESTA sessão (generate/upload/remove) já sobrescreve.
  mediaProvenance.value =
    data.ai_params?.generated_image && data.media_url ? 'ai' : null;
  if (data.ai_params) {
    const savedBrief = data.ai_params.brief || {};
    objective.value = savedBrief.objetivo || 'sell';
    brief.value =
      savedBrief.promover || (typeof savedBrief === 'string' ? savedBrief : '');
    offerCta.value = savedBrief.oferta_cta || '';
    tone.value = savedBrief.tom || data.ai_params.tone || 'professional';
    postFormat.value = savedBrief.formato || 'single';
    carouselCards.value = Number(savedBrief.cards) || 3;
    generateImage.value = !!data.ai_params.generated_image;
    variations.value = data.ai_params.variations || [];
    chosenVariation.value = data.ai_params.chosen_variation || 0;
    generationStatus.value = data.generation_status || 'ready';
    hasGenerated.value = true;
  }
};

const pollGeneratedPost = async id => {
  try {
    const { data } = await socialAPI.getPost(id);
    generationStatus.value = data.generation_status || 'ready';
    carouselMediaUrls.value = data.medias_urls || [];
    mediaPreviewUrl.value = carouselMediaUrls.value[0] || data.media_url || '';
    if (generationStatus.value === 'generating') {
      pollingTimer = window.setTimeout(() => pollGeneratedPost(id), 3000);
    }
  } catch (error) {
    generateErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.LOAD')
    );
  }
};

const loadPost = async id => {
  isLoadingPost.value = true;
  loadErrorMessage.value = '';
  try {
    const { data } = await socialAPI.getPost(id);
    applyLoadedPost(data);
    if (generationStatus.value === 'generating') pollGeneratedPost(data.id);
  } catch (error) {
    loadErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.LOAD')
    );
  } finally {
    isLoadingPost.value = false;
  }
};

const generate = async () => {
  if (!brief.value.trim()) {
    generateErrorMessage.value = t('SOCIAL_STUDIO.EDITOR.AI.BRIEF_REQUIRED');
    return;
  }
  generateErrorMessage.value = '';
  isGenerating.value = true;
  try {
    // Regenerar (2ª geração em diante nesta sessão) sempre cria outro post
    // NOVO no backend. Se o rascunho atual foi criado pela geração desta
    // sessão e ainda não foi salvo nem editado manualmente, apaga-o ANTES de
    // gerar de novo — best-effort, falha aqui não deve travar o fluxo
    // principal de gerar um novo criativo.
    const staleDraftId = unsavedGeneratedPostId.value;
    if (staleDraftId) {
      try {
        await socialAPI.deletePost(staleDraftId);
      } catch (cleanupError) {
        // silencioso de propósito — o pior caso é um rascunho órfão, não
        // vale travar a geração por causa disso.
      }
      if (currentPostId.value === staleDraftId) currentPostId.value = null;
      unsavedGeneratedPostId.value = null;
    }

    const { data } = await socialAPI.generatePost({
      objective: objective.value,
      promote: brief.value,
      offerCta: offerCta.value,
      tone: tone.value,
      format: postFormat.value,
      cards: postFormat.value === 'carousel' ? carouselCards.value : 1,
      networks: selectedAccountIds.value
        .map(
          id =>
            connectedAccounts.value.find(account => account.id === id)?.platform
        )
        .filter(Boolean),
      generateImage: generateImage.value,
      referenceImage: mediaIsVideo.value ? null : mediaFile.value,
    });
    // generate() sempre cria um post NOVO (o backend não aceita id
    // existente) — plataformas/agendamento já escolhidos pelo usuário
    // continuam locais e vão junto no próximo "Salvar rascunho".
    currentPostId.value = data.id;
    status.value = data.status;
    content.value = data.content || '';
    if (mediaFile.value && mediaPreviewUrl.value) {
      URL.revokeObjectURL(mediaPreviewUrl.value);
    }
    mediaFile.value = null;
    carouselMediaUrls.value = data.medias_urls || [];
    mediaPreviewUrl.value = data.media_url || carouselMediaUrls.value[0] || '';
    mediaProvenance.value =
      data.ai_params?.image_source === 'ai' && data.media_url ? 'ai' : null;
    variations.value = data.ai_params?.variations || [];
    chosenVariation.value = data.ai_params?.chosen_variation || 0;
    generationStatus.value = data.generation_status || 'ready';
    hasGenerated.value = true;
    unsavedGeneratedPostId.value = data.id;
    unsavedGeneratedContentSnapshot = content.value;
    if (generationStatus.value === 'generating') {
      pollingTimer = window.setTimeout(() => pollGeneratedPost(data.id), 3000);
    }
  } catch (error) {
    const fallback =
      error.response?.status === 503
        ? t('SOCIAL_STUDIO.EDITOR.AI.UNAVAILABLE_ERROR')
        : t('SOCIAL_STUDIO.EDITOR.ERRORS.GENERIC');
    generateErrorMessage.value = extractErrorMessage(error, fallback);
  } finally {
    isGenerating.value = false;
  }
};

const chooseVariation = async index => {
  if (!currentPostId.value || chosenVariation.value === index) return;
  generateErrorMessage.value = '';
  try {
    const { data } = await socialAPI.chooseVariation(
      currentPostId.value,
      index
    );
    chosenVariation.value = index;
    content.value = data.content || '';
    status.value = data.status;
    unsavedGeneratedContentSnapshot = content.value;
  } catch (error) {
    generateErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.GENERIC')
    );
  }
};

const triggerFileInput = () => fileInputRef.value?.click();

const onFileChange = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (file.type.startsWith('video/') && file.size > 300 * 1024 * 1024) {
    useAlert(t('SOCIAL_STUDIO.EDITOR.MEDIA.VIDEO_TOO_BIG'));
    return;
  }
  if (mediaFile.value && mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
  }
  mediaFile.value = file;
  mediaIsVideo.value = file.type.startsWith('video/');
  mediaPreviewUrl.value = URL.createObjectURL(file);
  mediaProvenance.value = 'manual';
  // Edição manual da mídia — o rascunho gerado deixa de ser "descartável".
  unsavedGeneratedPostId.value = null;
};

const removeLocalMedia = () => {
  if (mediaFile.value && mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
  }
  mediaFile.value = null;
  mediaIsVideo.value = false;
  mediaPreviewUrl.value = '';
  mediaProvenance.value = null;
  unsavedGeneratedPostId.value = null;
};

const togglePlatform = id => {
  // Edição manual das redes — o rascunho gerado deixa de ser "descartável".
  unsavedGeneratedPostId.value = null;
  selectedAccountIds.value = selectedAccountIds.value.includes(id)
    ? selectedAccountIds.value.filter(x => x !== id)
    : [...selectedAccountIds.value, id];
};

// Cria (ou atualiza) o registro com o estado atual do formulário. Usado
// tanto pelo "Salvar rascunho" quanto — para não aprovar/publicar um
// conteúdo diferente do que está na tela — antes de aprovar/publicar.
const persist = async () => {
  const payload = {
    content: content.value,
    scheduledAt: scheduledAt.value
      ? new Date(scheduledAt.value).toISOString()
      : '',
    platforms: selectedAccountIds.value,
    media: mediaFile.value || undefined,
    title: postTitle.value,
    productionStatus: productionStatus.value,
    driveLink: driveLink.value,
    briefing:
      creationMode.value === 'manual' ? manualBriefing.value : undefined,
  };
  const { data } = currentPostId.value
    ? await socialAPI.updatePost(currentPostId.value, payload)
    : await socialAPI.createPost(payload);

  currentPostId.value = data.id;
  status.value = data.status;
  if (mediaFile.value && mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
  }
  mediaFile.value = null;
  if (data.media_url) mediaPreviewUrl.value = data.media_url;
  // Salvo explicitamente pelo usuário — deixa de ser um rascunho gerado
  // "descartável" (regenerar não vai mais apagá-lo).
  unsavedGeneratedPostId.value = null;
  return data;
};

const saveDraft = async () => {
  if (!canSave.value) return;
  saveErrorMessage.value = '';
  isSavingDraft.value = true;
  try {
    const data = await persist();
    useAlert(t('SOCIAL_STUDIO.EDITOR.ACTIONS.SAVED_SUCCESS'));
    emit('saved', data);
  } catch (error) {
    saveErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.SAVE')
    );
  } finally {
    isSavingDraft.value = false;
  }
};

const approvePostAction = async () => {
  if (!canApprove.value || !canSave.value) return;
  actionErrorMessage.value = '';
  isApproving.value = true;
  try {
    await persist();
    const { data } = await socialAPI.approvePost(currentPostId.value);
    status.value = data.status;
    useAlert(t('SOCIAL_STUDIO.EDITOR.ACTIONS.APPROVED_SUCCESS'));
    emit('saved', data);
  } catch (error) {
    actionErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.APPROVE')
    );
  } finally {
    isApproving.value = false;
  }
};

const publishPostAction = async () => {
  if (!canPublish.value || !canSave.value) return;
  actionErrorMessage.value = '';
  isPublishing.value = true;
  try {
    await persist();
    const { data } = await socialAPI.publishPost(currentPostId.value);
    status.value = data.status;
    useAlert(
      scheduledAt.value
        ? t('SOCIAL_STUDIO.EDITOR.ACTIONS.SCHEDULE_SUCCESS')
        : t('SOCIAL_STUDIO.EDITOR.ACTIONS.PUBLISH_SUCCESS')
    );
    emit('saved', data);
    close();
  } catch (error) {
    actionErrorMessage.value = extractErrorMessage(
      error,
      t('SOCIAL_STUDIO.EDITOR.ERRORS.PUBLISH')
    );
  } finally {
    isPublishing.value = false;
  }
};

const open = (id = null) => {
  resetState();
  dialogRef.value?.open();
  fetchAccounts();
  const targetId = id ?? props.postId ?? null;
  if (targetId) loadPost(targetId);
};

const handleDialogClose = () => {
  emit('closed');
};

onBeforeUnmount(() => {
  if (pollingTimer) window.clearTimeout(pollingTimer);
  if (mediaFile.value && mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
  }
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="6xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleDialogClose"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ dialogTitle }}
        </h3>
        <p class="mt-0.5 text-xs text-n-slate-10">
          {{ t('SOCIAL_STUDIO.EDITOR.WORKSPACE_SUBTITLE') }}
        </p>
      </div>
      <span
        v-if="isEditing"
        class="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium"
        :class="statusTone"
      >
        {{ t(`SOCIAL_STUDIO.EDITOR.STATUS.${status.toUpperCase()}`) }}
      </span>
    </div>

    <div
      v-if="isLoadingPost"
      class="flex flex-col items-center justify-center gap-3 py-10"
    >
      <Spinner size="lg" />
      <p class="text-sm text-n-slate-11">
        {{ t('SOCIAL_STUDIO.EDITOR.LOADING_POST') }}
      </p>
    </div>

    <div
      v-else-if="loadErrorMessage"
      class="rounded-2xl border border-n-ruby-6 bg-n-ruby-2 p-4"
    >
      <p class="text-sm text-n-ruby-11">{{ loadErrorMessage }}</p>
    </div>

    <div
      v-else
      class="grid items-start gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.75fr)]"
    >
      <div class="flex flex-col gap-5 lg:pr-2">
        <!-- Escolha do modo (só na criação) -->
        <div v-if="!creationMode" class="grid gap-3 sm:grid-cols-2">
          <button
            class="flex flex-col items-start gap-2 rounded-2xl border border-n-weak bg-n-solid-2 p-5 text-left transition-colors hover:border-n-brand/50 hover:bg-n-alpha-1"
            @click="creationMode = 'ia'"
          >
            <span class="i-lucide-sparkles w-6 h-6 text-n-brand" />
            <span class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.EDITOR.MODE.AI_TITLE') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('SOCIAL_STUDIO.EDITOR.MODE.AI_DESC') }}
            </span>
          </button>
          <button
            class="flex flex-col items-start gap-2 rounded-2xl border border-n-weak bg-n-solid-2 p-5 text-left transition-colors hover:border-n-brand/50 hover:bg-n-alpha-1"
            @click="creationMode = 'manual'"
          >
            <span class="i-lucide-pencil-line w-6 h-6 text-n-teal-10" />
            <span class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.EDITOR.MODE.MANUAL_TITLE') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('SOCIAL_STUDIO.EDITOR.MODE.MANUAL_DESC') }}
            </span>
          </button>
        </div>

        <!-- Produção: título + status + link do Drive -->
        <section
          v-if="creationMode"
          class="rounded-2xl border border-n-weak bg-n-solid-2 p-4 grid gap-3 sm:grid-cols-[1fr_auto]"
        >
          <label class="flex flex-col gap-1 sm:col-span-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SOCIAL_STUDIO.EDITOR.PRODUCTION.TITLE_LABEL') }}
            </span>
            <input
              v-model="postTitle"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="t('SOCIAL_STUDIO.EDITOR.PRODUCTION.TITLE_PH')"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SOCIAL_STUDIO.EDITOR.PRODUCTION.STATUS_LABEL') }}
            </span>
            <select
              v-model="productionStatus"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            >
              <option value="">
                {{ t('SOCIAL_STUDIO.EDITOR.PRODUCTION.STATUS_NONE') }}
              </option>
              <option v-for="st in PRODUCTION_STATUSES" :key="st" :value="st">
                {{ t(`SOCIAL_STUDIO.PRODUCTION.STATUSES.${st}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SOCIAL_STUDIO.EDITOR.PRODUCTION.DRIVE_LABEL') }}
            </span>
            <input
              v-model="driveLink"
              type="url"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="t('SOCIAL_STUDIO.EDITOR.PRODUCTION.DRIVE_PH')"
            />
          </label>
        </section>

        <!-- Briefing (criação manual) -->
        <section
          v-if="creationMode === 'manual'"
          class="rounded-2xl border border-n-weak bg-n-solid-2 p-4"
        >
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SOCIAL_STUDIO.EDITOR.PRODUCTION.BRIEFING_LABEL') }}
            </span>
            <textarea
              v-model="manualBriefing"
              rows="3"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="t('SOCIAL_STUDIO.EDITOR.PRODUCTION.BRIEFING_PH')"
            />
          </label>
        </section>

        <!-- Geração por IA -->
        <section
          v-if="creationMode === 'ia'"
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2"
        >
          <div
            class="flex items-center gap-3 border-b border-n-weak bg-n-alpha-1 px-4 py-3"
          >
            <span
              class="flex size-8 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-sparkles size-4" />
            </span>
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.SECTION_TITLE') }}
              </h4>
              <p class="text-xs text-n-slate-10">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.SECTION_SUBTITLE') }}
              </p>
            </div>
          </div>

          <div class="space-y-5 p-4">
            <div class="space-y-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.OBJECTIVE_LABEL') }}
              </span>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button
                  v-for="option in OBJECTIVE_OPTIONS"
                  :key="option"
                  type="button"
                  class="rounded-xl border px-3 py-2 text-xs font-medium transition-colors"
                  :class="
                    objective === option
                      ? 'border-n-brand bg-n-brand/10 text-n-brand'
                      : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  :disabled="isGenerating"
                  @click="objective = option"
                >
                  {{
                    t(
                      `SOCIAL_STUDIO.EDITOR.AI.OBJECTIVE_${option.toUpperCase()}`
                    )
                  }}
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.FORMAT_LABEL') }}
              </span>
              <div class="grid gap-2 sm:grid-cols-2">
                <button
                  v-for="option in FORMAT_OPTIONS"
                  :key="option"
                  type="button"
                  class="flex items-center gap-3 rounded-xl border p-3 text-left transition-colors"
                  :class="
                    postFormat === option
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2'
                  "
                  :disabled="isGenerating"
                  @click="postFormat = option"
                >
                  <span
                    class="flex size-8 shrink-0 items-center justify-center rounded-lg"
                    :class="
                      postFormat === option
                        ? 'bg-n-brand/10 text-n-brand'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span
                      :class="
                        option === 'carousel'
                          ? 'i-lucide-gallery-horizontal-end'
                          : 'i-lucide-image'
                      "
                      class="size-4"
                    />
                  </span>
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          `SOCIAL_STUDIO.EDITOR.AI.FORMAT_${option.toUpperCase()}`
                        )
                      }}
                    </span>
                    <span class="block text-xs text-n-slate-10">
                      {{
                        t(
                          `SOCIAL_STUDIO.EDITOR.AI.FORMAT_${option.toUpperCase()}_HINT`
                        )
                      }}
                    </span>
                  </span>
                </button>
              </div>
            </div>

            <div
              v-if="postFormat === 'carousel'"
              class="rounded-xl border border-n-weak bg-n-alpha-1 p-3"
            >
              <div class="mb-2 flex items-center justify-between gap-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.CARDS_LABEL') }}
                </span>
                <span
                  class="rounded-lg bg-n-brand/10 px-2 py-1 text-xs font-semibold text-n-brand"
                >
                  {{
                    t('SOCIAL_STUDIO.EDITOR.AI.CARDS_COUNT', {
                      count: carouselCards,
                    })
                  }}
                </span>
              </div>
              <input
                v-model.number="carouselCards"
                type="range"
                min="2"
                max="10"
                step="1"
                class="w-full accent-n-brand"
                :disabled="isGenerating"
              />
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.CARDS_HINT') }}
              </p>
            </div>

            <TextArea
              v-model="brief"
              :label="t('SOCIAL_STUDIO.EDITOR.AI.PROMOTE_LABEL')"
              :placeholder="t('SOCIAL_STUDIO.EDITOR.AI.PROMOTE_PLACEHOLDER')"
              :disabled="isGenerating"
              min-height="4rem"
            />

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.OFFER_LABEL') }}
                </span>
                <input
                  v-model="offerCta"
                  type="text"
                  class="h-10 rounded-xl border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="t('SOCIAL_STUDIO.EDITOR.AI.OFFER_PLACEHOLDER')"
                  :disabled="isGenerating"
                />
              </label>

              <div class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.TONE_LABEL') }}
                </span>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="option in TONE_OPTIONS"
                    :key="option"
                    type="button"
                    class="rounded-lg border px-2.5 py-1.5 text-xs"
                    :class="
                      tone === option
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11'
                    "
                    :disabled="isGenerating"
                    @click="tone = option"
                  >
                    {{
                      t(`SOCIAL_STUDIO.EDITOR.AI.TONE_${option.toUpperCase()}`)
                    }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="postFormat === 'single'"
              class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.IMAGE_SOURCE_LABEL') }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{
                    mediaFile
                      ? t('SOCIAL_STUDIO.EDITOR.AI.REFERENCE_SELECTED')
                      : t('SOCIAL_STUDIO.EDITOR.AI.IMAGE_SOURCE_HINT')
                  }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  :label="t('SOCIAL_STUDIO.EDITOR.AI.USE_MY_ART')"
                  :disabled="isGenerating"
                  @click="triggerFileInput"
                />
                <label v-if="!mediaFile" class="flex items-center gap-2">
                  <span class="text-xs text-n-slate-11">
                    {{ t('SOCIAL_STUDIO.EDITOR.AI.GENERATE_IMAGE_LABEL') }}
                  </span>
                  <Switch v-model="generateImage" :disabled="isGenerating" />
                </label>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                size="sm"
                :label="generationCostLabel"
                :is-loading="isGenerating"
                :disabled="isGenerating"
                @click="generate"
              />
              <p v-if="isGenerating" class="text-xs text-n-slate-10">
                {{ generatingHint }}
              </p>
            </div>

            <p v-if="generateErrorMessage" class="text-xs text-n-ruby-11">
              {{ generateErrorMessage }}
            </p>

            <div
              v-if="variations.length"
              class="space-y-2 border-t border-n-weak pt-4"
            >
              <div class="flex items-center justify-between gap-3">
                <div>
                  <h5 class="text-sm font-semibold text-n-slate-12">
                    {{ t('SOCIAL_STUDIO.EDITOR.AI.VARIATIONS_TITLE') }}
                  </h5>
                  <p class="text-xs text-n-slate-10">
                    {{ t('SOCIAL_STUDIO.EDITOR.AI.VARIATIONS_HINT') }}
                  </p>
                </div>
                <span class="text-xs font-medium text-n-slate-10">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.VARIATIONS_COUNT') }}
                </span>
              </div>
              <div class="grid gap-2 sm:grid-cols-3">
                <button
                  v-for="(variation, index) in variations"
                  :key="`${variation.headline}-${index}`"
                  type="button"
                  class="flex min-h-32 flex-col rounded-xl border p-3 text-left transition-colors"
                  :class="
                    chosenVariation === index
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2'
                  "
                  @click="chooseVariation(index)"
                >
                  <span
                    class="mb-1 line-clamp-1 text-sm font-semibold text-n-slate-12"
                  >
                    {{ variation.headline }}
                  </span>
                  <span
                    class="line-clamp-3 flex-1 text-xs leading-5 text-n-slate-10"
                  >
                    {{ variation.body }}
                  </span>
                  <span class="mt-2 text-xs font-semibold text-n-brand">
                    {{
                      chosenVariation === index
                        ? t('SOCIAL_STUDIO.EDITOR.AI.VARIATION_SELECTED')
                        : t('SOCIAL_STUDIO.EDITOR.AI.USE_VARIATION')
                    }}
                  </span>
                </button>
              </div>
            </div>

            <div
              v-if="
                postFormat === 'carousel' && generationStatus === 'generating'
              "
              class="flex items-center gap-3 rounded-xl border border-n-amber-6 bg-n-amber-2 p-3"
            >
              <Spinner size="sm" />
              <div>
                <p class="text-sm font-medium text-n-amber-11">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.CAROUSEL_GENERATING') }}
                </p>
                <p class="text-xs text-n-amber-10">
                  {{ t('SOCIAL_STUDIO.EDITOR.AI.CAROUSEL_GENERATING_HINT') }}
                </p>
              </div>
            </div>

            <div v-if="carouselMediaUrls.length" class="space-y-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('SOCIAL_STUDIO.EDITOR.AI.CAROUSEL_PREVIEW') }}
              </span>
              <div class="flex gap-2 overflow-x-auto pb-1">
                <div
                  v-for="(url, index) in carouselMediaUrls"
                  :key="url"
                  class="relative size-24 shrink-0 overflow-hidden rounded-xl border border-n-weak bg-n-alpha-2"
                >
                  <img :src="url" class="size-full object-cover" alt="" />
                  <span
                    class="absolute bottom-1 left-1 rounded-md bg-n-solid-1/90 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11"
                  >
                    {{ index + 1 }}
                  </span>
                </div>
              </div>
            </div>

            <p
              v-if="hasGenerated && !isGenerating && !variations.length"
              class="text-xs text-n-slate-10"
            >
              {{ t('SOCIAL_STUDIO.EDITOR.AI.GENERATED_HINT') }}
            </p>
          </div>
        </section>

        <!-- Conteúdo -->
        <TextArea
          v-if="creationMode"
          v-model="content"
          :label="t('SOCIAL_STUDIO.EDITOR.CONTENT_LABEL')"
          :placeholder="t('SOCIAL_STUDIO.EDITOR.CONTENT_PLACEHOLDER')"
          min-height="6rem"
        />

        <!-- Mídia -->
        <div v-if="creationMode" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SOCIAL_STUDIO.EDITOR.MEDIA.LABEL') }}
          </span>
          <div v-if="mediaPreviewUrl" class="relative w-40">
            <video
              v-if="mediaIsVideo"
              :src="mediaPreviewUrl"
              controls
              muted
              playsinline
              class="h-40 w-40 rounded-xl border border-n-weak object-cover"
            />
            <img
              v-else
              :src="mediaPreviewUrl"
              class="h-40 w-40 rounded-xl border border-n-weak object-cover"
              :alt="t('SOCIAL_STUDIO.EDITOR.MEDIA.LABEL')"
            />
            <span
              v-if="showAiGeneratedBadge"
              class="absolute left-1.5 top-1.5 rounded-full bg-n-solid-1/90 px-2 py-0.5 text-[10px] font-medium text-n-slate-11"
            >
              {{ t('SOCIAL_STUDIO.EDITOR.MEDIA.AI_GENERATED_BADGE') }}
            </span>
            <button
              v-if="isLocalMediaFile"
              type="button"
              class="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-n-solid-1/90 text-n-slate-11 hover:text-n-ruby-11"
              :aria-label="t('SOCIAL_STUDIO.EDITOR.MEDIA.REMOVE')"
              @click="removeLocalMedia"
            >
              <span class="i-lucide-x size-3" />
            </button>
          </div>
          <div>
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*,video/*"
              class="hidden"
              @change="onFileChange"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              :label="
                mediaPreviewUrl
                  ? t('SOCIAL_STUDIO.EDITOR.MEDIA.REPLACE')
                  : t('SOCIAL_STUDIO.EDITOR.MEDIA.UPLOAD')
              "
              @click="triggerFileInput"
            />
          </div>
        </div>

        <!-- Redes -->
        <div v-if="creationMode" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SOCIAL_STUDIO.EDITOR.PLATFORMS.LABEL') }}
          </span>
          <p v-if="accountsLoadError" class="text-xs text-n-ruby-11">
            {{ accountsLoadError }}
          </p>
          <div
            v-else-if="isLoadingAccounts"
            class="flex items-center gap-2 text-sm text-n-slate-10"
          >
            <Spinner size="xs" />
            {{ t('SOCIAL_STUDIO.EDITOR.PLATFORMS.LOADING') }}
          </div>
          <div
            v-else-if="connectedAccounts.length === 0"
            class="rounded-xl border border-n-weak bg-n-solid-2 p-3 text-sm text-n-slate-11"
          >
            {{ t('SOCIAL_STUDIO.EDITOR.PLATFORMS.NONE_CONNECTED') }}
            <RouterLink
              :to="accountScopedRoute('settings_inbox_new')"
              class="font-semibold text-n-brand underline underline-offset-2 hover:opacity-80"
            >
              {{ t('SOCIAL_STUDIO.EDITOR.PLATFORMS.CONNECT_CTA') }}
            </RouterLink>
          </div>
          <div v-else class="flex flex-col gap-2">
            <label
              v-for="account in connectedAccounts"
              :key="account.id"
              class="flex cursor-pointer items-center gap-3 rounded-xl border border-n-weak p-2.5"
            >
              <Checkbox
                :model-value="selectedAccountIds.includes(account.id)"
                @update:model-value="() => togglePlatform(account.id)"
              />
              <span
                class="flex size-7 shrink-0 items-center justify-center rounded-lg"
                :class="[
                  platformMeta(account.platform)?.badgeClass,
                  platformMeta(account.platform)?.iconTone || 'text-white',
                ]"
              >
                <span
                  :class="platformMeta(account.platform)?.icon"
                  class="size-3.5"
                />
              </span>
              <span class="truncate text-sm text-n-slate-12">
                {{
                  account.username ||
                  t(
                    `SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${account.platform.toUpperCase()}`
                  )
                }}
              </span>
            </label>
          </div>
        </div>

        <!-- Agendamento -->
        <div v-if="creationMode" class="flex flex-col gap-1">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="social-post-scheduled-at"
          >
            {{ t('SOCIAL_STUDIO.EDITOR.SCHEDULE.LABEL') }}
          </label>
          <input
            id="social-post-scheduled-at"
            v-model="scheduledAt"
            type="datetime-local"
            class="block w-full reset-base text-sm outline outline-1 outline-n-weak border-none rounded-lg bg-n-alpha-black2 px-3 py-2 text-n-slate-12 focus:outline-n-brand [color-scheme:light] dark:[color-scheme:dark]"
          />
          <p class="text-xs text-n-slate-10">
            {{ t('SOCIAL_STUDIO.EDITOR.SCHEDULE.HINT') }}
          </p>
        </div>

        <p v-if="saveErrorMessage" class="text-sm text-n-ruby-11">
          {{ saveErrorMessage }}
        </p>
        <p v-if="actionErrorMessage" class="text-sm text-n-ruby-11">
          {{ actionErrorMessage }}
        </p>
      </div>

      <aside class="space-y-3 lg:sticky lg:top-0">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.TITLE') }}
            </h4>
            <p class="text-xs text-n-slate-10">
              {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.SUBTITLE') }}
            </p>
          </div>
          <span
            class="rounded-full border border-n-weak bg-n-alpha-2 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.LIVE') }}
          </span>
        </div>
        <FeedPreview
          :content="content"
          :media-urls="previewMediaUrls"
          :media-is-video="mediaIsVideo"
          :username="previewAccount?.username"
          :platform="previewAccount?.platform || 'instagram'"
        />
        <div
          class="grid grid-cols-3 divide-x divide-n-weak rounded-xl border border-n-weak bg-n-alpha-1 py-2"
        >
          <div class="px-2 text-center">
            <p class="text-[10px] uppercase tracking-wide text-n-slate-9">
              {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.FORMAT') }}
            </p>
            <p class="mt-0.5 text-xs font-semibold text-n-slate-11">
              {{
                t(`SOCIAL_STUDIO.EDITOR.AI.FORMAT_${postFormat.toUpperCase()}`)
              }}
            </p>
          </div>
          <div class="px-2 text-center">
            <p class="text-[10px] uppercase tracking-wide text-n-slate-9">
              {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.MEDIA') }}
            </p>
            <p class="mt-0.5 text-xs font-semibold text-n-slate-11">
              {{ previewMediaUrls.length || 0 }}
            </p>
          </div>
          <div class="px-2 text-center">
            <p class="text-[10px] uppercase tracking-wide text-n-slate-9">
              {{ t('SOCIAL_STUDIO.EDITOR.PREVIEW.NETWORKS') }}
            </p>
            <p class="mt-0.5 text-xs font-semibold text-n-slate-11">
              {{ selectedAccountIds.length }}
            </p>
          </div>
        </div>
      </aside>
    </div>

    <template #footer>
      <div
        v-if="!isLoadingPost && !loadErrorMessage"
        class="flex flex-wrap items-center justify-end gap-2"
      >
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('SOCIAL_STUDIO.EDITOR.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          type="button"
          variant="outline"
          :label="
            isSavingDraft
              ? t('SOCIAL_STUDIO.EDITOR.ACTIONS.SAVING')
              : t('SOCIAL_STUDIO.EDITOR.ACTIONS.SAVE_DRAFT')
          "
          :is-loading="isSavingDraft"
          :disabled="!canSave || isSavingDraft"
          @click="saveDraft"
        />
        <Button
          v-if="canApprove"
          type="button"
          color="blue"
          :label="
            isApproving
              ? t('SOCIAL_STUDIO.EDITOR.ACTIONS.APPROVING')
              : t('SOCIAL_STUDIO.EDITOR.ACTIONS.APPROVE')
          "
          :is-loading="isApproving"
          :disabled="!canSave || isApproving"
          @click="approvePostAction"
        />
        <Button
          v-if="status === 'approved' && isAdmin && isEditing"
          type="button"
          color="teal"
          :label="
            isPublishing
              ? t('SOCIAL_STUDIO.EDITOR.ACTIONS.PUBLISHING')
              : publishActionLabel
          "
          :is-loading="isPublishing"
          :disabled="!canPublish || isPublishing"
          @click="publishPostAction"
        />
      </div>
    </template>
  </Dialog>
</template>
